import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, forkJoin, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';

import { AuthService } from '../security/auth.service';
import { BaseMasterDataService } from '../services/base-master-data.service';
import { SessionStorageService } from '../services/session-storage.service';
import { ToolbarService } from '../services/toolbar.service';
import { Country } from '../models/common/country.model';
import { Company } from '../company/company.model';
import { Customer } from '../customer/customer.model';
import {
    CompanyPlanCode,
    CompanyPlanDefinition,
    CompanyPlanSubscription,
    CompanyPlanUsage,
    TrialOnboardingStartRequest,
    TrialOnboardingStartResponse
} from './enterprise-subscription.model';
import { EnterpriseSubscriptionService } from './enterprise-subscription.service';

const TALK_TO_SALES_URL = 'https://cargomate.co/logi-link/talk-to-sales';

interface WorkspaceProvisioningStep {
    title: string;
    detail: string;
    icon: string;
}

const WORKSPACE_PROVISIONING_STEPS: readonly WorkspaceProvisioningStep[] = [
    { title: 'Provisioning your workspace', detail: 'Creating your company space and administrator access.', icon: 'pi pi-building' },
    { title: 'Setting up tax codes', detail: 'Adding the finance rules your workspace needs.', icon: 'pi pi-percentage' },
    { title: 'Creating your chart of accounts', detail: 'Preparing the accounts that power your financial reporting.', icon: 'pi pi-list' },
    { title: 'Adding logistics service products', detail: 'Loading the services your operation can deliver.', icon: 'pi pi-truck' },
    { title: 'Creating accounting journals', detail: 'Preparing sales, purchase, bank, cash, and expense journals.', icon: 'pi pi-book' },
    { title: 'Configuring finance settings', detail: 'Applying workforce, bulk-cost, and order-control defaults.', icon: 'pi pi-cog' },
    { title: 'Preparing payment controls', detail: 'Applying payable matching rules and your default cost centre.', icon: 'pi pi-wallet' },
    { title: 'Mapping fleet dimensions', detail: 'Connecting fleet accounting details for the road ahead.', icon: 'pi pi-car' },
    { title: 'Enabling manual-entry controls', detail: 'Putting reliable accounting guardrails in place.', icon: 'pi pi-shield' },
    { title: 'Building your workflows', detail: 'Loading default workflow resources, statuses, and transitions.', icon: 'pi pi-sitemap' },
    { title: 'Adding workforce categories', detail: 'Preparing the people setup your team will use.', icon: 'pi pi-users' },
    { title: 'Stocking warehouse master data', detail: 'Creating warehouse, region, zone, and item-condition defaults.', icon: 'pi pi-warehouse' },
    { title: 'Loading POSM setup data', detail: 'Preparing cost types and planning defaults.', icon: 'pi pi-briefcase' },
    { title: 'Setting up field work templates', detail: 'Making on-the-ground execution ready from day one.', icon: 'pi pi-map-marker' },
    { title: 'Applying role presets', detail: 'Giving your new workspace the right starting permissions.', icon: 'pi pi-id-card' },
    { title: 'Loading report templates', detail: 'Finishing the dashboards and reports your team can use.', icon: 'pi pi-chart-bar' },
    { title: 'Putting on the finishing touches', detail: 'Saving your setup so your workspace is ready to go.', icon: 'pi pi-sparkles' }
];

@Component({
    selector: 'app-enterprise-subscription',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        ToastModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        SelectModule
    ],
    templateUrl: './enterprise-subscription.component.html',
    styleUrls: ['./enterprise-subscription.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EnterpriseSubscriptionComponent implements OnInit, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly subscriptions = inject(EnterpriseSubscriptionService);
    private readonly baseMasterDataService = inject(BaseMasterDataService);
    private readonly sessionStorage = inject(SessionStorageService);
    private readonly toolbarService = inject(ToolbarService);
    private readonly messageService = inject(MessageService);
    private provisioningProgressTimer: ReturnType<typeof setInterval> | null = null;

    readonly managementMode = signal(false);
    readonly catalog = signal<CompanyPlanDefinition[]>([]);
    readonly catalogLoading = signal(true);
    readonly catalogError = signal<string | null>(null);
    readonly selectedPlan = signal<CompanyPlanDefinition | null>(null);
    readonly currentPlan = signal<CompanyPlanSubscription | null>(null);
    readonly usage = signal<CompanyPlanUsage | null>(null);
    readonly countries = signal<Country[]>([]);
    readonly loading = signal(false);
    readonly registering = signal(false);
    readonly showRegistration = signal(false);
    readonly provisioningStepIndex = signal(0);

    readonly selectedPlanCode = computed(() => this.selectedPlan()?.code ?? null);
    readonly currentCompany = computed(() => this.sessionStorage.getCompany() ?? this.sessionStorage.getCustomer()?.company);
    readonly passwordsMatch = computed(() => this.adminForm.value.password === this.adminForm.value.confirmPassword);
    readonly businessTypeOptions = [
        { label: 'Integrated 3PL', value: 'INTEGRATED_3PL' },
        { label: 'Carrier', value: 'CARRIER' },
        { label: 'Freight Forwarder', value: 'FREIGHT_FORWARDER' },
        { label: 'Customs Broker', value: 'CUSTOMS_BROKER' }
    ];
    readonly countryOptions = computed(() => this.countries().map(country => ({
        label: country.name,
        value: String(country.id)
    })));
    readonly provisioningSteps = WORKSPACE_PROVISIONING_STEPS;
    readonly provisioningStep = computed(() => this.provisioningSteps[this.provisioningStepIndex()] ?? this.provisioningSteps[0]);
    readonly provisioningProgress = computed(() => Math.min(96, Math.round(((this.provisioningStepIndex() + 1) / this.provisioningSteps.length) * 100)));
    readonly provisioningStepWindow = computed(() => {
        const activeIndex = this.provisioningStepIndex();
        const start = Math.min(Math.max(0, activeIndex - 1), Math.max(0, this.provisioningSteps.length - 3));
        return this.provisioningSteps.slice(start, start + 3).map((step, index) => ({ ...step, index: start + index }));
    });

    readonly companyForm = this.fb.group({
        name: ['', Validators.required],
        businessType: ['INTEGRATED_3PL', Validators.required],
        crNumber: ['', Validators.required],
        taxRegNumber: ['', Validators.required],
        emailAddress: ['', Validators.email],
        phoneNumber: [''],
        address: ['', Validators.required],
        countryId: ['', Validators.required],
        websiteUrl: ['']
    });

    readonly adminForm = this.fb.group({
        fullName: ['', Validators.required],
        emailAddress: ['', [Validators.required, Validators.email]],
        phoneNumber: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required]
    });

    ngOnInit(): void {
        this.managementMode.set(this.route.snapshot.data['mode'] === 'management');
        this.loadCatalog();
        if (this.managementMode()) {
            this.loadCurrentPlan();
        } else {
            this.loadCountries();
        }
    }

    ngOnDestroy(): void {
        this.stopProvisioningProgress();
    }

    selectPlan(plan: CompanyPlanDefinition): void {
        if (plan.code === 'ENTERPRISE') {
            window.open(TALK_TO_SALES_URL, '_blank', 'noopener,noreferrer');
            return;
        }
        this.selectedPlan.set(plan);
        if (!this.managementMode()) {
            this.showRegistration.set(true);
            queueMicrotask(() => document.getElementById('company-registration')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
            return;
        }
        this.changePlan(plan);
    }

    cancelRegistration(): void {
        if (this.registering()) return;
        this.showRegistration.set(false);
        this.selectedPlan.set(null);
    }

    submitRegistration(): void {
        this.companyForm.markAllAsTouched();
        this.adminForm.markAllAsTouched();
        const plan = this.selectedPlan();
        if (!plan || this.companyForm.invalid || this.adminForm.invalid || !this.passwordsMatch()) {
            this.messageService.add({ severity: 'warn', summary: 'Complete the registration', detail: 'Choose a plan and complete all required company and administrator fields.' });
            return;
        }

        this.startProvisioningProgress();
        this.registering.set(true);
        queueMicrotask(() => document.getElementById('workspace-provisioning')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
        const admin = this.adminForm.getRawValue();
        this.authService.registerOrSignIn(admin.emailAddress!, admin.password!).pipe(
            switchMap(user => {
                if (!user?.uid) return throwError(() => new Error('Unable to create the administrator login.'));
                return from(user.getIdToken(true)).pipe(map(token => ({ user, token })));
            }),
            tap(({ token }) => {
                this.sessionStorage.saveAccessToken(token, Date.now());
                this.sessionStorage.resetBrowserSessionId();
            }),
            switchMap(({ user }) => this.authService.activateCurrentBrowserSession().pipe(map(() => user))),
            switchMap(user => this.subscriptions.startTrialOnboarding(this.buildOnboardingRequest(user.uid))),
            map(response => {
                if (!response.success || !response.data) throw new Error(response.message || 'Unable to activate the selected plan.');
                return response.data;
            }),
            finalize(() => {
                this.stopProvisioningProgress();
                this.registering.set(false);
            })
        ).subscribe({
            next: response => {
                this.applyOnboardingResponse(response, admin.emailAddress!, admin.fullName!);
                this.messageService.add({ severity: 'success', summary: 'Workspace ready', detail: `${response.planSubscription?.plan?.name ?? plan.name} is active.` });
                this.router.navigate([response.nextRoute || '/dashboard']);
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Registration failed', detail: this.errorMessage(error) })
        });
    }

    planButtonLabel(plan: CompanyPlanDefinition): string {
        if (plan.code === 'ENTERPRISE') return 'Talk to Sales';
        if (!this.managementMode()) return plan.code === 'FREE' ? 'Start Free' : 'Start 30-Day Free Trial';
        const current = this.currentPlan();
        if (current?.planCode === plan.code && current.status !== 'PENDING_PAYMENT') {
            return current.status === 'TRIAL' ? 'Trial Active' : 'Current Plan';
        }
        return plan.code === 'FREE' ? 'Use Free Plan' : 'Start Trial / Upgrade';
    }

    isPlanDisabled(plan: CompanyPlanDefinition): boolean {
        const current = this.currentPlan();
        return this.loading() || (current?.planCode === plan.code && current.status !== 'PENDING_PAYMENT');
    }

    displayLimit(limit: number | null | undefined, label: string): string {
        return limit == null ? `Unlimited ${label}` : `${limit.toLocaleString()} ${label}`;
    }

    trialDaysRemaining(): number | null {
        const trialEnd = this.currentPlan()?.trialEndsAt;
        if (!trialEnd) return null;
        return Math.max(0, Math.ceil((new Date(trialEnd).getTime() - Date.now()) / 86_400_000));
    }

    retryCatalog(): void {
        this.loadCatalog();
    }

    private loadCatalog(): void {
        this.catalogLoading.set(true);
        this.catalogError.set(null);
        this.subscriptions.getCompanyPlanCatalog().pipe(
            finalize(() => this.catalogLoading.set(false))
        ).subscribe({
            next: catalog => {
                this.catalog.set(catalog ?? []);
                if (!catalog?.length) {
                    this.catalogError.set('Plans are temporarily unavailable. Please try again.');
                }
            },
            error: () => {
                this.catalog.set([]);
                this.catalogError.set('Plans could not be loaded. Please check your connection and retry.');
            }
        });
    }

    private startProvisioningProgress(): void {
        this.stopProvisioningProgress();
        this.provisioningStepIndex.set(0);
        this.provisioningProgressTimer = setInterval(() => {
            this.provisioningStepIndex.update(index => Math.min(index + 1, this.provisioningSteps.length - 1));
        }, 6000);
    }

    private stopProvisioningProgress(): void {
        if (this.provisioningProgressTimer != null) {
            clearInterval(this.provisioningProgressTimer);
            this.provisioningProgressTimer = null;
        }
    }

    private loadCountries(): void {
        this.baseMasterDataService.getCountries().pipe(catchError(() => of([]))).subscribe(countries => {
            this.countries.set(countries);
            if (countries.length === 1) this.companyForm.patchValue({ countryId: String(countries[0].id) });
        });
    }

    private loadCurrentPlan(): void {
        const company = this.currentCompany();
        if (!company?.id) return;
        this.loading.set(true);
        forkJoin({
            plan: this.subscriptions.getEffectiveCompanyPlan(company.id),
            usage: this.subscriptions.getCompanyPlanUsage(company.id)
        }).pipe(finalize(() => this.loading.set(false))).subscribe({
            next: ({ plan, usage }) => {
                this.currentPlan.set(plan);
                this.usage.set(usage);
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Unable to load plan', detail: this.errorMessage(error) })
        });
    }

    private changePlan(plan: CompanyPlanDefinition): void {
        const company = this.currentCompany();
        if (!company?.id) {
            this.messageService.add({ severity: 'warn', summary: 'Company session required', detail: 'Sign in to change the company plan.' });
            return;
        }
        this.loading.set(true);
        const request = plan.code === 'FREE'
            ? this.subscriptions.activateFreePlan(company.id)
            : this.subscriptions.startCompanyPlanTrial(company.id, plan.code).pipe(
                catchError(() => this.subscriptions.checkoutCompanyPlan(company.id, plan.code).pipe(
                    map(response => {
                        if (!response.success || !response.data) throw new Error(response.message || 'Unable to create checkout.');
                        window.location.assign(response.data);
                        return null;
                    })
                ))
            );
        request.pipe(finalize(() => this.loading.set(false))).subscribe({
            next: subscription => {
                if (!subscription) return;
                this.currentPlan.set(subscription);
                this.selectedPlan.set(subscription.plan);
                this.subscriptions.getCompanyPlanUsage(company.id).subscribe(usage => this.usage.set(usage));
                this.messageService.add({ severity: 'success', summary: 'Plan updated', detail: `${subscription.plan.name} is now active.` });
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Unable to update plan', detail: this.errorMessage(error) })
        });
    }

    private buildOnboardingRequest(firebaseId: string): TrialOnboardingStartRequest {
        const company = this.companyForm.getRawValue();
        const admin = this.adminForm.getRawValue();
        return {
            existingFirebaseId: firebaseId,
            idempotencyKey: ['company-plan', firebaseId, company.crNumber, company.taxRegNumber, this.selectedPlanCode()].join(':').toLowerCase(),
            cleanupFirebaseOnFailure: true,
            businessType: company.businessType || 'INTEGRATED_3PL',
            selectedPlanCode: this.selectedPlanCode()!,
            admin: {
                fullName: admin.fullName!, emailAddress: admin.emailAddress!, phoneNumber: admin.phoneNumber!, defaultLanguage: 'en-US'
            },
            company: {
                name: company.name!, crNumber: Number(company.crNumber), taxRegNumber: company.taxRegNumber!,
                phoneNumber: company.phoneNumber || null, websiteURL: company.websiteUrl || null,
                emailAddress: company.emailAddress || null, address: company.address!, placeId: null, logo: null,
                location: null, countryId: Number(company.countryId)
            }
        };
    }

    private applyOnboardingResponse(response: TrialOnboardingStartResponse, email: string, name: string): void {
        const company = response.company as Company;
        const customer = response.adminCustomer as Customer;
        customer.company = customer.company ?? company;
        customer.roles = response.roles ?? [];
        this.sessionStorage.saveUser({ uid: customer.firebaseId, email, username: name, roles: customer.roles.map(role => role.roleName) });
        this.sessionStorage.saveCustomer(customer);
        this.sessionStorage.saveCompany(company);
        this.toolbarService.updateCustomer(customer);
        this.toolbarService.updateLoggedInStatus(true);
    }

    private errorMessage(error: any): string {
        return error?.error?.message || error?.message || 'Please try again or contact support.';
    }
}
